import type { Order } from '@/api/orders'

export type OfflineActionType = '出發' | '客上' | '客下' | '簽名'

export interface OfflineAction {
  id: string
  order_id: string
  action: OfflineActionType
  payload: Record<string, any>
  created_at: string
  date_key: string
}

const QUEUE_KEY = 'driver_offline_action_queue'
const CACHE_PREFIX = 'driver_offline_orders_cache_'

const todayKey = () => new Date().toISOString().split('T')[0]

/** 判斷是否為「連不上網路」造成的失敗（非伺服器回應的錯誤，如 4xx/5xx 不算） */
export const isNetworkError = (error: any): boolean => {
  return !error?.response
}

export const getQueue = (): OfflineAction[] => {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]')
  } catch {
    return []
  }
}

const saveQueue = (queue: OfflineAction[]) => {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(queue))
}

export const enqueueAction = (order_id: string, action: OfflineActionType, payload: Record<string, any>): OfflineAction => {
  const entry: OfflineAction = {
    id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    order_id,
    action,
    payload,
    created_at: new Date().toISOString(),
    date_key: todayKey(),
  }
  const queue = getQueue()
  queue.push(entry)
  saveQueue(queue)
  return entry
}

export const removeAction = (id: string) => {
  saveQueue(getQueue().filter(a => a.id !== id))
}

/** 只保留今天的待同步動作，過期（非今日）的直接捨棄 */
export const cleanupStaleActions = () => {
  const today = todayKey()
  const queue = getQueue().filter(a => a.date_key === today)
  saveQueue(queue)
}

export const getPendingActionsForOrder = (order_id: string): OfflineAction[] =>
  getQueue().filter(a => a.order_id === order_id)

export const cacheOrders = (dateFilter: string, orders: Order[]) => {
  try {
    localStorage.setItem(
      `${CACHE_PREFIX}${dateFilter}`,
      JSON.stringify({ orders, cached_at: new Date().toISOString() })
    )
  } catch {
    // localStorage 滿了就算了，離線快取本來就是盡力而為
  }
}

export const getCachedOrders = (dateFilter: string): { orders: Order[]; cached_at: string } | null => {
  try {
    const raw = localStorage.getItem(`${CACHE_PREFIX}${dateFilter}`)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}
